import { mat4, vec3 } from 'gl-matrix';
import { Camera } from '../camera';
import { Context } from '../context/Context';
import { GltfAnimation } from '../animation';
import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../renderer';
import { RenderObject } from '../object/RenderObject';
import { BoneCollisionBody } from '../physics/BoneCollisionBody';
import { SkinnedDebugShader } from '../shader/generated/SkinnedDebugShader.generated';

const FLOATS_PER_VERTEX = 15; // pos(3) + color(4) + jointWeights(4) + jointIndices(4)
const STRIDE_BYTES = FLOATS_PER_VERTEX * 4;

const JOINT_COLOR: [number, number, number, number] = [0.0, 0.9, 0.9, 1.0];
const LINE_COLOR: [number, number, number, number] = [0.6, 0.6, 0.6, 0.8];
const CAPSULE_COLOR: [number, number, number, number] = [0.0, 0.6, 0.8, 0.3];

const JOINT_OCTAHEDRON_SIZE_FACTOR = 0.015;
const MIN_JOINT_OCTAHEDRON_SIZE = 0.01;
const CAPSULE_SEGMENTS = 8;
const CAPSULE_LAT_BANDS = 2;
const SPHERE_SEGMENTS = 8;
const SPHERE_LAT_BANDS = 4;

/**
 * GPU-skinned skeleton + bone-collision visualizer.
 *
 * Builds the entire debug mesh ONCE in mesh-local bind-pose space and tags
 * each vertex with the joint index it should follow. The vertex shader
 * (SkinnedDebugShader) does the per-frame bone transform via the existing
 * animation data texture, so per-frame CPU cost is just a handful of
 * uniform setters and one draw call.
 *
 * Conventions:
 *  - Joint octahedron markers are tagged with their own joint index.
 *  - Parent→child line segments are tagged with the PARENT joint index, so
 *    both endpoints rigidly follow the parent (the child joint's runtime
 *    position is determined by the parent's transform composed with the
 *    constant local bone offset, since glTF skin animations don't keyframe
 *    joint translations).
 *  - Capsule-shape collision capsules: tagged with the parent joint index,
 *    same reasoning.
 *  - Sphere-shape collision capsules (root or leaf bones): tagged with the
 *    capsule's own joint index.
 */
export class SkinnedSkeletonVisualizer implements DrawableObject {
  public renderConfiguration: RenderConfiguration = {
    order: 6000,
    culling: 'none',
    static: false,
    modes: [RenderMode.Overlay],
  };

  private lineBuffer: WebGLBuffer;
  private lineVao: WebGLVertexArrayObject;
  private lineVertexCount: number;

  private triBuffer: WebGLBuffer;
  private triVao: WebGLVertexArrayObject;
  private triVertexCount: number;

  constructor(
    private ctx: Context,
    private renderObject: RenderObject,
    private body?: BoneCollisionBody,
  ) {
    const gl = ctx.gl;
    const anim = renderObject.animation as GltfAnimation;
    const bindPositions = anim.computeBindPosePositions();

    // The geometry lives in mesh-local bind-pose space and gets scaled by
    // the model matrix at draw time, so we divide world-space sizes here by
    // the model's uniform scale to keep on-screen sizes constant across
    // models with different scales.
    const modelScale = renderObject.trs.scale || 1;
    const invScale = 1 / modelScale;
    const jointSize =
      Math.max(
        MIN_JOINT_OCTAHEDRON_SIZE,
        renderObject.bounds.boundingSphere.radius * JOINT_OCTAHEDRON_SIZE_FACTOR,
      ) * invScale;

    const lineData = this.buildLineGeometry(anim, bindPositions);
    const triData = this.buildTriGeometry(anim, bindPositions, jointSize);

    this.lineVertexCount = lineData.length / FLOATS_PER_VERTEX;
    this.triVertexCount = triData.length / FLOATS_PER_VERTEX;

    this.lineBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, lineData, gl.STATIC_DRAW);
    this.lineVao = this.createVAO(this.lineBuffer);

    this.triBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.triBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, triData, gl.STATIC_DRAW);
    this.triVao = this.createVAO(this.triBuffer);
  }

  public render(ctx: Context, camera: Camera, _settings: RenderSettings): void {
    if (this.lineVertexCount === 0 && this.triVertexCount === 0) return;

    const ro = this.renderObject;
    if (!ro.isAnimated || !ro.animationState) return;

    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getSkinnedDebugShader();
    ctx.setShader(shader);

    const modelViewMatrix = mat4.multiply(mat4.create(), camera.modelViewMatrix, ro.modelMatrix);
    shader.uniformsContainer
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(modelViewMatrix);

    this.bindAnimationUniforms(shader);

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    if (this.lineVertexCount > 0) {
      gl.bindVertexArray(this.lineVao);
      gl.drawArrays(gl.LINES, 0, this.lineVertexCount);
    }
    if (this.triVertexCount > 0) {
      gl.bindVertexArray(this.triVao);
      gl.drawArrays(gl.TRIANGLES, 0, this.triVertexCount);
    }

    gl.disable(gl.BLEND);
    gl.enable(gl.DEPTH_TEST);
  }

  public destroy(): void {
    const gl = this.ctx.gl;
    gl.deleteBuffer(this.lineBuffer);
    gl.deleteBuffer(this.triBuffer);
    gl.deleteVertexArray(this.lineVao);
    gl.deleteVertexArray(this.triVao);
  }

  // ─────────────────────────────────────────────────────────── animation uniforms

  private bindAnimationUniforms(shader: SkinnedDebugShader): void {
    const ro = this.renderObject;
    const anim = ro.animation as GltfAnimation;
    const dataTexture = ro.mesh.animationDataTexture!;
    const frameSet = ro.animationState!.getFrames();
    const offset = dataTexture.getFrameOffset(frameSet.state, frameSet.frameIndex);
    const dim = dataTexture.dimension;

    shader.uniformsContainer
      .setBindShapeMatrix(anim.bindShapeMatrix)
      .setDataSettings([dim.width, dim.height, offset])
      .setMatrixSampler(dataTexture);

    if (frameSet.blend) {
      const blendOffset = dataTexture.getFrameOffset(
        frameSet.blend.state,
        frameSet.blend.frameIndex,
      );
      shader.uniformsContainer.setBlendSettings([
        dim.width,
        dim.height,
        blendOffset,
        frameSet.blend.weight,
      ]);
    } else {
      shader.uniformsContainer.setBlendSettings([0, 0, 0, 0]);
    }

    // Bone overrides — mirror RenderObject.setupAnimation lines 425-443.
    // Always set the count (even when zero) since our shader is always
    // compiled with NOODLE_BONE_OVERRIDES and the loop reads stale uniforms otherwise.
    if (ro.boneOverrides && ro.boneOverrides.size > 0) {
      let i = 0;
      for (const [jointIndex, jointMatrix] of ro.boneOverrides) {
        if (i >= 32) break;
        const joint = anim.joints[jointIndex];
        const invBind = joint.frames[frameSet.state][0].invBindMatrix;
        const shaderMatrix = mat4.create();
        mat4.multiply(shaderMatrix, jointMatrix, invBind);
        mat4.transpose(shaderMatrix, shaderMatrix);
        shader.uniformsContainer.setBoneOverrides(i, shaderMatrix);
        shader.uniformsContainer.setBoneOverrideIndices(i, jointIndex);
        i++;
      }
      shader.uniformsContainer.setBoneOverrideCount(i);
    } else {
      shader.uniformsContainer.setBoneOverrideCount(0);
    }
  }

  // ─────────────────────────────────────────────────────────── geometry building

  private buildLineGeometry(anim: GltfAnimation, bindPositions: vec3[]): Float32Array {
    // Allocate upper bound: 2 vertices per parent→child link.
    const linkCount = anim.jointParentMap.reduce((n, p) => (p >= 0 ? n + 1 : n), 0);
    const data = new Float32Array(linkCount * 2 * FLOATS_PER_VERTEX);
    const writer = new VertexWriter(data);

    for (let i = 0; i < anim.jointParentMap.length; i++) {
      const parentIdx = anim.jointParentMap[i];
      if (parentIdx < 0) continue;
      // Both endpoints follow the parent joint (see class doc comment).
      writer.push(bindPositions[parentIdx], LINE_COLOR, parentIdx);
      writer.push(bindPositions[i], LINE_COLOR, parentIdx);
    }

    return writer.trim();
  }

  private buildTriGeometry(
    anim: GltfAnimation,
    bindPositions: vec3[],
    jointSize: number,
  ): Float32Array {
    // Worst-case sizing: octahedrons + spheres + capsules. Estimate generously
    // and trim. Each octahedron = 24 verts. Each sphere = SPHERE_SEGMENTS *
    // SPHERE_LAT_BANDS * 6 verts. Each capsule = SEGMENTS*6 (cylinder) +
    // SEGMENTS*LAT_BANDS*12 (caps).
    const jointCount = anim.joints.length;
    const capsuleCount = this.body?.boneCapsules.length ?? 0;
    const octahedronVerts = jointCount * 24;
    const sphereVerts = capsuleCount * SPHERE_SEGMENTS * SPHERE_LAT_BANDS * 6;
    const capsuleVerts =
      capsuleCount * (CAPSULE_SEGMENTS * 6 + CAPSULE_SEGMENTS * CAPSULE_LAT_BANDS * 12);
    const upperBound = (octahedronVerts + sphereVerts + capsuleVerts) * FLOATS_PER_VERTEX;

    const data = new Float32Array(upperBound);
    const writer = new VertexWriter(data);

    // Joint markers: octahedron at every joint, tagged with that joint's index.
    for (let i = 0; i < jointCount; i++) {
      writeOctahedron(writer, bindPositions[i], jointSize, JOINT_COLOR, i);
    }

    // Collision shapes (spheres for roots/leaves, capsules between parent→child).
    // Only drawn when a BoneCollisionBody is supplied — otherwise just joints + bone lines.
    if (!this.body) return writer.trim();
    for (const cap of this.body.boneCapsules) {
      if (cap.radius < 0.001) continue;

      if (cap.shape === 'sphere' || cap.parentJointIndex < 0) {
        // Sphere centered at the joint, follows the joint itself.
        writeSphere(writer, bindPositions[cap.jointIndex], cap.radius, CAPSULE_COLOR, cap.jointIndex);
      } else {
        // Capsule from parent → child, both ends follow the parent joint.
        writeCapsule(
          writer,
          bindPositions[cap.parentJointIndex],
          bindPositions[cap.jointIndex],
          cap.radius,
          CAPSULE_COLOR,
          cap.parentJointIndex,
        );
      }
    }

    return writer.trim();
  }

  // ─────────────────────────────────────────────────────────── VAO setup

  private createVAO(buffer: WebGLBuffer): WebGLVertexArrayObject {
    const gl = this.ctx.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

    // Match the unified attribute location map (mesh/Mesh.ts:32-55):
    // 0=position, 3=color, 4=jointWeights, 5=jointIndices
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, STRIDE_BYTES, 0);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, STRIDE_BYTES, 3 * 4);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.FLOAT, false, STRIDE_BYTES, 7 * 4);
    gl.enableVertexAttribArray(5);
    gl.vertexAttribPointer(5, 4, gl.FLOAT, false, STRIDE_BYTES, 11 * 4);

    gl.bindVertexArray(null);
    return vao;
  }
}

// ─────────────────────────────────────────────────────────── helpers

/**
 * Writes interleaved (pos + color + jointWeights + jointIndices) vertices into
 * a pre-sized Float32Array via a moving cursor — no dynamic allocations.
 */
class VertexWriter {
  public cursor = 0;
  constructor(private data: Float32Array) {}

  push(pos: vec3, color: [number, number, number, number], jointIndex: number): void {
    const c = this.cursor;
    const d = this.data;
    d[c + 0] = pos[0];
    d[c + 1] = pos[1];
    d[c + 2] = pos[2];
    d[c + 3] = color[0];
    d[c + 4] = color[1];
    d[c + 5] = color[2];
    d[c + 6] = color[3];
    // jointWeights (only first slot used)
    d[c + 7] = 1;
    d[c + 8] = 0;
    d[c + 9] = 0;
    d[c + 10] = 0;
    // jointIndices (only first slot used)
    d[c + 11] = jointIndex;
    d[c + 12] = 0;
    d[c + 13] = 0;
    d[c + 14] = 0;
    this.cursor += FLOATS_PER_VERTEX;
  }

  pushXYZ(
    x: number,
    y: number,
    z: number,
    color: [number, number, number, number],
    jointIndex: number,
  ): void {
    const c = this.cursor;
    const d = this.data;
    d[c + 0] = x;
    d[c + 1] = y;
    d[c + 2] = z;
    d[c + 3] = color[0];
    d[c + 4] = color[1];
    d[c + 5] = color[2];
    d[c + 6] = color[3];
    d[c + 7] = 1;
    d[c + 8] = 0;
    d[c + 9] = 0;
    d[c + 10] = 0;
    d[c + 11] = jointIndex;
    d[c + 12] = 0;
    d[c + 13] = 0;
    d[c + 14] = 0;
    this.cursor += FLOATS_PER_VERTEX;
  }

  trim(): Float32Array {
    return this.data.subarray(0, this.cursor);
  }
}

function writeOctahedron(
  writer: VertexWriter,
  center: vec3,
  size: number,
  color: [number, number, number, number],
  jointIndex: number,
): void {
  const cx = center[0];
  const cy = center[1];
  const cz = center[2];
  const s = size;
  // 6 corner points
  const top: [number, number, number] = [cx, cy + s, cz];
  const bot: [number, number, number] = [cx, cy - s, cz];
  const fr: [number, number, number] = [cx, cy, cz + s];
  const ba: [number, number, number] = [cx, cy, cz - s];
  const le: [number, number, number] = [cx - s, cy, cz];
  const ri: [number, number, number] = [cx + s, cy, cz];

  // 8 triangle faces
  const faces: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ][] = [
    [top, fr, ri],
    [top, ri, ba],
    [top, ba, le],
    [top, le, fr],
    [bot, ri, fr],
    [bot, ba, ri],
    [bot, le, ba],
    [bot, fr, le],
  ];

  for (const [a, b, c] of faces) {
    writer.pushXYZ(a[0], a[1], a[2], color, jointIndex);
    writer.pushXYZ(b[0], b[1], b[2], color, jointIndex);
    writer.pushXYZ(c[0], c[1], c[2], color, jointIndex);
  }
}

function writeSphere(
  writer: VertexWriter,
  center: vec3,
  radius: number,
  color: [number, number, number, number],
  jointIndex: number,
): void {
  // Two-axis lat/long sphere — fine for low-segment debug.
  const cx = center[0];
  const cy = center[1];
  const cz = center[2];

  for (let lat = 0; lat < SPHERE_LAT_BANDS; lat++) {
    const phi0 = (lat / SPHERE_LAT_BANDS) * Math.PI - Math.PI / 2;
    const phi1 = ((lat + 1) / SPHERE_LAT_BANDS) * Math.PI - Math.PI / 2;
    const cP0 = Math.cos(phi0);
    const sP0 = Math.sin(phi0);
    const cP1 = Math.cos(phi1);
    const sP1 = Math.sin(phi1);

    for (let i = 0; i < SPHERE_SEGMENTS; i++) {
      const theta0 = (i / SPHERE_SEGMENTS) * Math.PI * 2;
      const theta1 = ((i + 1) / SPHERE_SEGMENTS) * Math.PI * 2;
      const cT0 = Math.cos(theta0);
      const sT0 = Math.sin(theta0);
      const cT1 = Math.cos(theta1);
      const sT1 = Math.sin(theta1);

      const x00 = cx + radius * cP0 * cT0;
      const y00 = cy + radius * sP0;
      const z00 = cz + radius * cP0 * sT0;
      const x10 = cx + radius * cP1 * cT0;
      const y10 = cy + radius * sP1;
      const z10 = cz + radius * cP1 * sT0;
      const x01 = cx + radius * cP0 * cT1;
      const y01 = cy + radius * sP0;
      const z01 = cz + radius * cP0 * sT1;
      const x11 = cx + radius * cP1 * cT1;
      const y11 = cy + radius * sP1;
      const z11 = cz + radius * cP1 * sT1;

      writer.pushXYZ(x00, y00, z00, color, jointIndex);
      writer.pushXYZ(x10, y10, z10, color, jointIndex);
      writer.pushXYZ(x11, y11, z11, color, jointIndex);
      writer.pushXYZ(x00, y00, z00, color, jointIndex);
      writer.pushXYZ(x11, y11, z11, color, jointIndex);
      writer.pushXYZ(x01, y01, z01, color, jointIndex);
    }
  }
}

const TMP_AXIS = vec3.create();
const TMP_AXIS_DIR = vec3.create();
const TMP_NEG_AXIS = vec3.create();
const TMP_U = vec3.create();
const TMP_V = vec3.create();

function writeCapsule(
  writer: VertexWriter,
  a: vec3,
  b: vec3,
  radius: number,
  color: [number, number, number, number],
  jointIndex: number,
): void {
  vec3.sub(TMP_AXIS, b, a);
  const axisLen = vec3.length(TMP_AXIS);
  if (axisLen < 0.0001) {
    writeSphere(writer, a, radius, color, jointIndex);
    return;
  }
  vec3.scale(TMP_AXIS_DIR, TMP_AXIS, 1 / axisLen);
  vec3.negate(TMP_NEG_AXIS, TMP_AXIS_DIR);

  // Build orthonormal basis perpendicular to the axis.
  if (Math.abs(TMP_AXIS_DIR[1]) < 0.9) {
    vec3.cross(TMP_U, TMP_AXIS_DIR, [0, 1, 0]);
  } else {
    vec3.cross(TMP_U, TMP_AXIS_DIR, [1, 0, 0]);
  }
  vec3.normalize(TMP_U, TMP_U);
  vec3.cross(TMP_V, TMP_AXIS_DIR, TMP_U);

  const ux = TMP_U[0];
  const uy = TMP_U[1];
  const uz = TMP_U[2];
  const vx = TMP_V[0];
  const vy = TMP_V[1];
  const vz = TMP_V[2];

  // Cylinder body — quads from ringA→ringB
  for (let i = 0; i < CAPSULE_SEGMENTS; i++) {
    const t0 = (i / CAPSULE_SEGMENTS) * Math.PI * 2;
    const t1 = ((i + 1) / CAPSULE_SEGMENTS) * Math.PI * 2;
    const c0 = Math.cos(t0) * radius;
    const s0 = Math.sin(t0) * radius;
    const c1 = Math.cos(t1) * radius;
    const s1 = Math.sin(t1) * radius;

    const a0x = a[0] + ux * c0 + vx * s0;
    const a0y = a[1] + uy * c0 + vy * s0;
    const a0z = a[2] + uz * c0 + vz * s0;
    const a1x = a[0] + ux * c1 + vx * s1;
    const a1y = a[1] + uy * c1 + vy * s1;
    const a1z = a[2] + uz * c1 + vz * s1;
    const b0x = b[0] + ux * c0 + vx * s0;
    const b0y = b[1] + uy * c0 + vy * s0;
    const b0z = b[2] + uz * c0 + vz * s0;
    const b1x = b[0] + ux * c1 + vx * s1;
    const b1y = b[1] + uy * c1 + vy * s1;
    const b1z = b[2] + uz * c1 + vz * s1;

    writer.pushXYZ(a0x, a0y, a0z, color, jointIndex);
    writer.pushXYZ(b0x, b0y, b0z, color, jointIndex);
    writer.pushXYZ(b1x, b1y, b1z, color, jointIndex);
    writer.pushXYZ(a0x, a0y, a0z, color, jointIndex);
    writer.pushXYZ(b1x, b1y, b1z, color, jointIndex);
    writer.pushXYZ(a1x, a1y, a1z, color, jointIndex);
  }

  // Hemisphere caps
  writeHemisphere(writer, b, TMP_AXIS_DIR, ux, uy, uz, vx, vy, vz, radius, color, jointIndex, true);
  writeHemisphere(writer, a, TMP_NEG_AXIS, ux, uy, uz, vx, vy, vz, radius, color, jointIndex, false);
}

function writeHemisphere(
  writer: VertexWriter,
  center: vec3,
  axis: vec3,
  ux: number,
  uy: number,
  uz: number,
  vx: number,
  vy: number,
  vz: number,
  radius: number,
  color: [number, number, number, number],
  jointIndex: number,
  facingPositive: boolean,
): void {
  const cx = center[0];
  const cy = center[1];
  const cz = center[2];
  const ax = axis[0];
  const ay = axis[1];
  const az = axis[2];

  for (let lat = 0; lat < CAPSULE_LAT_BANDS; lat++) {
    const phi0 = (lat / CAPSULE_LAT_BANDS) * (Math.PI / 2);
    const phi1 = ((lat + 1) / CAPSULE_LAT_BANDS) * (Math.PI / 2);
    const cP0 = Math.cos(phi0);
    const sP0 = Math.sin(phi0);
    const cP1 = Math.cos(phi1);
    const sP1 = Math.sin(phi1);

    for (let i = 0; i < CAPSULE_SEGMENTS; i++) {
      const t0 = (i / CAPSULE_SEGMENTS) * Math.PI * 2;
      const t1 = ((i + 1) / CAPSULE_SEGMENTS) * Math.PI * 2;
      const cT0 = Math.cos(t0);
      const sT0 = Math.sin(t0);
      const cT1 = Math.cos(t1);
      const sT1 = Math.sin(t1);

      const x00 = cx + radius * (cP0 * (ux * cT0 + vx * sT0) + sP0 * ax);
      const y00 = cy + radius * (cP0 * (uy * cT0 + vy * sT0) + sP0 * ay);
      const z00 = cz + radius * (cP0 * (uz * cT0 + vz * sT0) + sP0 * az);
      const x10 = cx + radius * (cP1 * (ux * cT0 + vx * sT0) + sP1 * ax);
      const y10 = cy + radius * (cP1 * (uy * cT0 + vy * sT0) + sP1 * ay);
      const z10 = cz + radius * (cP1 * (uz * cT0 + vz * sT0) + sP1 * az);
      const x01 = cx + radius * (cP0 * (ux * cT1 + vx * sT1) + sP0 * ax);
      const y01 = cy + radius * (cP0 * (uy * cT1 + vy * sT1) + sP0 * ay);
      const z01 = cz + radius * (cP0 * (uz * cT1 + vz * sT1) + sP0 * az);
      const x11 = cx + radius * (cP1 * (ux * cT1 + vx * sT1) + sP1 * ax);
      const y11 = cy + radius * (cP1 * (uy * cT1 + vy * sT1) + sP1 * ay);
      const z11 = cz + radius * (cP1 * (uz * cT1 + vz * sT1) + sP1 * az);

      if (facingPositive) {
        writer.pushXYZ(x00, y00, z00, color, jointIndex);
        writer.pushXYZ(x10, y10, z10, color, jointIndex);
        writer.pushXYZ(x11, y11, z11, color, jointIndex);
        writer.pushXYZ(x00, y00, z00, color, jointIndex);
        writer.pushXYZ(x11, y11, z11, color, jointIndex);
        writer.pushXYZ(x01, y01, z01, color, jointIndex);
      } else {
        // Reverse winding for the opposite cap
        writer.pushXYZ(x00, y00, z00, color, jointIndex);
        writer.pushXYZ(x11, y11, z11, color, jointIndex);
        writer.pushXYZ(x10, y10, z10, color, jointIndex);
        writer.pushXYZ(x00, y00, z00, color, jointIndex);
        writer.pushXYZ(x01, y01, z01, color, jointIndex);
        writer.pushXYZ(x11, y11, z11, color, jointIndex);
      }
    }
  }
}

